'use client';

import type { ReactNode } from 'react';
import type { IToggle } from 'unleash-proxy-client';
// Since QueryClientProvider relies on useContext under the hood, we have to put 'use client' on top
import {
  isServer,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';

import IntercomWidget from '@/components/entities/analytics/components/IntercomWidget/IntercomWidget';
import AppStartupEffect from '@/components/features/layout/AppStartupEffect';
import { UnleashProvider } from '@/components/features/layout/UnleashProvider';
import { WebSocketProvider } from '@/contexts/websockets/websocket-provider';
import { ReduxProvider } from '@/redux/redux-provider';

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Default staleTime for user/profile queries that need to refetch
        // SSR queries will override this with staleTime: Infinity
        staleTime: 60 * 1000, // 1 minute
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

function getQueryClient() {
  // TODO: Need to figure out better way how to handle this, because now it's marked deprecated
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient();
    }
    return browserQueryClient;
  }
}

type ProvidersProps = {
  children: ReactNode;
  unleashBootstrap?: IToggle[];
};

export default function Providers({ children, unleashBootstrap }: ProvidersProps) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();

  return (
    <UnleashProvider bootstrap={unleashBootstrap}>
      <PostHogProvider client={posthog}>
        <ReduxProvider>
          <QueryClientProvider client={queryClient}>
            <WebSocketProvider>
              <AppStartupEffect />
              <IntercomWidget>
                {children}
              </IntercomWidget>
            </WebSocketProvider>
          </QueryClientProvider>
        </ReduxProvider>
      </PostHogProvider>
    </UnleashProvider>
  );
}
